import React, { useEffect } from "react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { injectJsonLd, breadcrumbSchema } from "../../seo";

const features = [
  {
    title: "Appointment Booking & Reminders",
    description:
      "Let patients book, reschedule, or cancel appointments 24/7 via your website or WhatsApp. Automated reminders reduce no-shows significantly.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
      </svg>
    ),
  },
  {
    title: "Clinic & Service Information",
    description:
      "Answer questions about operating hours, available doctors, services offered, panel insurance, and parking — without tying up your front desk staff.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
    ),
  },
  {
    title: "Pre-Visit Symptom Triage",
    description:
      "Collect patient symptoms before their appointment and route urgent cases to the right department. Reduces triage time at the front desk.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
  },
  {
    title: "Panel Insurance Verification",
    description:
      "Patients frequently ask which insurance panels are accepted. Your AI provides accurate, up-to-date panel lists and guides them to the right contact if needed.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    title: "Post-Visit Follow-Up",
    description:
      "Automatically send follow-up messages after appointments to check on patient recovery and remind them of next steps or medication schedules.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
  },
  {
    title: "BM, English & Chinese",
    description:
      "Serve patients in their preferred language. Critical for Malaysian healthcare providers serving patients from diverse linguistic backgrounds.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
      </svg>
    ),
  },
];

const useCases = [
  {
    title: "GP & Specialist Clinics",
    description: "Free up front desk staff from repetitive appointment and panel queries. Let them focus on patients who are physically present.",
  },
  {
    title: "Dental & Aesthetic Clinics",
    description: "Handle treatment enquiries, pricing questions, and appointment bookings automatically — even during off-hours.",
  },
  {
    title: "Wellness & Physiotherapy",
    description: "Capture new patient leads 24/7 with an AI that explains your services, packages, and books consultations on the spot.",
  },
];

export function HealthcarePage() {
  useEffect(() => {
    document.title = "AI Chatbot for Clinics & Healthcare — Malaysia | Onyx AI";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Automate appointment bookings, panel insurance queries & patient FAQs for your clinic. AI chatbot in BM, EN & Chinese for Malaysian healthcare providers."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://onyxai.chat/solutions/healthcare");
    }
    return injectJsonLd("breadcrumb-jsonld", breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Solutions", path: "/solutions" },
      { name: "Healthcare", path: "/solutions/healthcare" },
    ]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-[#6a5a7a] mb-10">
            <a href="/" className="hover:text-[#a078d0] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#6a5a7a]">Solutions</span>
            <span>/</span>
            <span className="text-[#8a7a9a]">Healthcare</span>
          </nav>

          <div className="mb-16">
            <p className="text-[#a078d0] text-sm font-medium uppercase tracking-widest mb-4">
              Healthcare & Clinics
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
              AI Chatbot for Clinics & Healthcare Providers
            </h1>
            <p className="text-[#8a7a9a] text-lg max-w-2xl">
              Automate appointment bookings, answer patient FAQs, and handle panel insurance queries. Free your front desk to focus on patients — not the phone.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/signup"
                className="inline-block px-7 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
              >
                Start Free Trial
              </a>
              <a
                href="/pricing"
                className="inline-block px-7 py-3 border border-[#2a2040] hover:border-[#a078d0] text-[#8a7a9a] hover:text-white font-medium rounded-lg transition-colors"
              >
                View Pricing
              </a>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-6">What Your AI Chatbot Handles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {features.map((feature) => (
              <div key={feature.title} className="p-6 rounded-xl onyx-card onyx-glow-hover">
                <div className="w-10 h-10 rounded-lg bg-[#a078d0]/10 flex items-center justify-center text-[#c0a0e0] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-white mb-6">Common Use Cases</h2>
          <div className="grid md:grid-cols-3 gap-5 mb-16">
            {useCases.map((item) => (
              <div key={item.title} className="p-5 rounded-xl onyx-card">
                <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="p-8 rounded-2xl onyx-card text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Give your patients better care, starting with better communication.</h2>
            <p className="text-[#8a7a9a] mb-6">Set up in under 5 minutes. No developers needed.</p>
            <a
              href="/signup"
              className="inline-block px-8 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
            >
              Get Started Free
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
